/**
 * Phase 5.9.14 — Forgot Password page.
 *
 * Enter email → backend always returns 200 (no enumeration leak) → show
 * a generic "if an account exists, we sent a link" message regardless.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, ArrowLeft } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
    } catch {
      // Even on error — show success to prevent enumeration.
    }
    setSent(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050003] p-4">
      <div className="w-full max-w-md bg-[#0C0612] border border-[#2A0F3D] p-6 rounded">
        <h1 className="text-[#39FF14] font-mono text-sm mb-2">// ASH & MERCH</h1>
        <h2 className="text-white text-xl mb-2" data-testid="forgot-page-title">Forgot password</h2>
        {sent ? (
          <div data-testid="forgot-sent-message" className="space-y-4">
            <p className="text-[#A3C7A8] text-sm">
              If an account exists for that email, we sent a reset link. Check your inbox (and spam folder).
            </p>
            <p className="text-[#6B8E73] text-xs">
              The link expires in 60 minutes. You can request another anytime.
            </p>
            <Link to="/login" data-testid="forgot-back-to-login" className="inline-flex items-center text-[#39FF14] hover:underline text-sm font-mono">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-[#A3C7A8] text-sm mb-4">
              Enter the email tied to your account. We'll send a reset link.
            </p>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
              className="bg-black/40 border-[#2A0F3D] text-white"
              data-testid="forgot-email-input"
            />
            <Button
              type="submit"
              disabled={loading || !email.trim()}
              className="w-full bg-[#39FF14] text-black hover:bg-[#4AF626] font-bold rounded-none"
              data-testid="forgot-submit-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              {loading ? 'SENDING…' : 'SEND RESET LINK'}
            </Button>
            <Link to="/login" data-testid="forgot-back-to-login" className="inline-flex items-center text-[#6B8E73] hover:text-[#39FF14] text-xs font-mono">
              <ArrowLeft className="w-3 h-3 mr-1" /> Back to login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
